
import { Component, Prop, Watch } from 'vue-property-decorator';
import ListFilters from 'ah-common-lib/src/common/components/listing/ListFilters.vue';
import CurrencyFilterSelect from 'ah-trades/src/components/currency/CurrencyFilterSelect.vue';
import { selectField } from 'ah-common-lib/src/form/models';
import { makeFormModel } from 'ah-common-lib/src/form/helpers';
import { DateRange } from 'ah-common-lib/src/common/components/DateSelector.vue';
import {
  purposeTypesToHuman,
  HedgingInstruments,
  TradeState,
  PurposeTypes,
  CommissionDisplayType,
  TradeStatus,
  formatHedgingInstrument,
} from 'ah-api-gateways';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  subWeeks,
  subMonths,
  subYears,
  startOfToday,
  endOfToday,
} from 'date-fns';
import DateSelector from 'ah-common-lib/src/common/components/DateSelector.vue';
import { tradeStatusBadges } from '../../../../ah-api-gateways/models/trade/tradeStatus';
import { FormModel } from 'ah-common-lib/src/form/interfaces';

const displayOptions = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 },
];

const datePickerChoices = [
  { label: 'Today', start: startOfToday(), end: endOfToday() },
  { label: 'This week', start: startOfWeek(startOfToday()), end: endOfWeek(endOfToday()) },
  { label: 'Past 7 days', start: subWeeks(startOfToday(), 1), end: endOfToday() },
  { label: 'This month', start: startOfMonth(startOfToday()), end: endOfMonth(endOfToday()) },
  {
    label: 'Last month',
    start: startOfMonth(subMonths(startOfToday(), 1)),
    end: endOfMonth(subMonths(endOfToday(), 1)),
  },
  {
    label: 'Last 3 month',
    start: startOfMonth(subMonths(startOfToday(), 3)),
    end: endOfMonth(subMonths(endOfToday(), 1)),
  },
  {
    label: 'Last 6 month',
    start: startOfMonth(subMonths(startOfToday(), 6)),
    end: endOfMonth(subMonths(endOfToday(), 1)),
  },
  { label: 'Last 1 year', start: startOfYear(subYears(startOfToday(), 1)), end: endOfYear(subYears(endOfToday(), 1)) },
  { label: 'All', start: startOfYear(subYears(startOfToday(), 5)), end: endOfYear(endOfToday()) },
];

@Component({
  components: {
    CurrencyFilterSelect,
    DateSelector,
  },
})
export default class TradeReportListFilters extends ListFilters {
  /**
   * Whether to show state filters
   *
   * Any value other than false is considered truthy
   */
  @Prop({ default: false }) showStates!: boolean | string;

  /**
   * Whether to show date filters
   *
   * Any value other than false is considered truthy
   */
  @Prop({ default: false }) showDates!: boolean | string;

  /**
   * Whether to show the search box for searching by trade id
   * (or client name, if allowClientNameSearch is truthy)
   *
   * Any value other than false is considered truthy
   */
  @Prop({ default: false }) showSearch!: boolean | string;

  /**
   * Whether to hide the page size selector
   * Defaults to false, any value other than false is considered truthy "
   */

  @Prop({ default: false }) hidePageSize!: string | boolean;

  /**
   * Which hedging instruments to allow
   *
   */
  @Prop({ required: false }) possibleInstruments?: HedgingInstruments[];

  /**
   * Which purpose types to allow
   *
   */
  @Prop({ required: false }) possiblePurposes?: PurposeTypes[];

  /**
   * Whether to allow searching by client name
   * Has no effect if showSearch is false
   *
   * Any value other than false is considered truthy
   */
  @Prop({ default: false }) allowClientNameSearch!: boolean | string;

  /**
   * Whether a trade is pre searched
   *
   * This value should be the same as the trade reference number
   */
  @Prop({ required: false }) openTrade?: string;

  private tradeStatusForm: FormModel | null = null;

  private currencyPairForm: FormModel | null = null;

  private instrumentForm: FormModel | null = null;

  private purposeTypeForm: FormModel | null = null;

  private displayForm: FormModel | null = null;

  private tradeStatus: TradeState[] = [];

  private referenceNumberOrClientName = '';

  private queryBy = ['clientName', 'composedReferenceNumber'];

  private tradeDate: DateRange | null = null;

  private settlementDate: DateRange | null = null;

  private accountsFilter: CommissionDisplayType = this.filters.commissionDisplayType ?? CommissionDisplayType.ORIGINAL;

  created() {
    this.tradeStatusForm = makeFormModel({
      name: 'tradeFm',
      fieldType: 'form',
      fields: [
        selectField('tdStatus', 'Trade Status', this.tradeStatusOptions, {
          required: false,
          clearable: true,
          clearValue: '',
          placeholder: 'All',
        }),
      ],
    });

    this.currencyPairForm = makeFormModel({
      name: 'currencyPairFm',
      fieldType: 'form',
      fields: [
        selectField('currencyPair', 'Currency Pair', () => this.currencyPairs, {
          required: false,
          clearable: true,
          clearValue: '',
          placeholder: 'All',
        }),
      ],
    });

    this.instrumentForm = makeFormModel({
      name: 'instrumentFm',
      fieldType: 'form',
      fields: [
        selectField('instrumentFm', 'Instrument', this.instrumentOptions, {
          required: false,
          clearable: true,
          clearValue: '',
          placeholder: 'All',
        }),
      ],
    });

    this.purposeTypeForm = makeFormModel({
      name: 'purposeTypeFm',
      fieldType: 'form',
      fields: [
        selectField('purposeType', 'Purpose Type', this.purposeTypeOptions, {
          required: false,
          clearable: true,
          clearValue: '',
          placeholder: 'All',
        }),
      ],
    });

    this.displayForm = makeFormModel({
      name: 'displayFm',
      fieldType: 'form',
      fields: [
        selectField('pageSize', 'Display', displayOptions, {
          required: false,
          defaultValue: 10,
        }),
      ],
    });

    this.$ahTradesState.store.useSettingsStore().loadTradeableCurrencies();
  }

  private get currencyPairs() {
    return this.$ahTradesState.store.useSettingsStore().currencyPairs.map((i) => ({ label: i, value: i }));
  }

  private get showPageSize() {
    return this.hidePageSize === false;
  }

  private get commonDateChoices() {
    return datePickerChoices;
  }

  private get CommissionDisplayType() {
    return CommissionDisplayType;
  }

  private get instrumentOptions() {
    const instruments: HedgingInstruments[] = [
      HedgingInstruments.FX_SPOT,
      HedgingInstruments.FX_FORWARD,
      HedgingInstruments.WINDOW_FORWARD,
      HedgingInstruments.FLEXIBLE_FORWARD,
      HedgingInstruments.VANILLA_OPTIONS,
    ];

    return instruments
      .filter((k) => !this.possibleInstruments?.length || this.possibleInstruments.includes(k))
      .map((k) => {
        return {
          value: k,
          label: formatHedgingInstrument(k),
        };
      });
  }

  private get purposeTypeOptions() {
    const purpose: PurposeTypes[] = [PurposeTypes.OPENING_TRADE, PurposeTypes.DRAWDOWN, PurposeTypes.ROLL_FORWARD];

    return purpose
      .filter((k) => !this.possiblePurposes?.length || this.possiblePurposes.includes(k))
      .map((k) => {
        return {
          value: k,
          label: purposeTypesToHuman[k],
        };
      });
  }

  private get tradeStatusOptions() {
    return (Object.keys(tradeStatusBadges) as TradeStatus[]).map((k) => {
      return {
        value: k,
        label: tradeStatusBadges[k].label,
      };
    });
  }

  get isApportioned() {
    return this.accountsFilter === CommissionDisplayType.APPORTIONED;
  }

  get knownFilters() {
    return [
      {
        key: 'instrument',
        getter: () => this.instrumentForm?.instrumentFm || undefined,
        setter: (value: any) => (this.instrumentForm!.instrumentFm = value),
      },
      {
        key: 'status',
        getter: () => this.tradeStatusForm?.tdStatus || undefined,
        setter: (value: any) => (this.tradeStatusForm!.tdStatus = value),
      },
      {
        key: 'currencyPair',
        getter: () => this.currencyPairForm?.currencyPair || undefined,
        setter: (value: any) => (this.currencyPairForm!.currencyPair = value),
      },
      {
        key: 'purposeType',
        getter: () => this.purposeTypeForm?.purposeType || undefined,
        setter: (value: any) => (this.purposeTypeForm!.purposeType = value),
      },
      {
        key: 'tradeDateFrom',
        getter: () => {
          if (this.tradeDate?.start && !isNaN(new Date(this.tradeDate.start).valueOf())) {
            return startOfDay(this.tradeDate.start);
          }
        },
        setter: (value: any) => {
          if (this.tradeDate) {
            this.tradeDate.start = startOfDay(new Date(value));
          }
        },
      },
      {
        key: 'tradeDateTo',
        getter: () => {
          if (this.tradeDate?.end && !isNaN(new Date(this.tradeDate.end).valueOf())) {
            return endOfDay(this.tradeDate.end);
          }
        },
        setter: (value: any) => {
          if (this.tradeDate) {
            this.tradeDate.end = endOfDay(new Date(value));
          }
        },
      },
      {
        key: 'settlementDateFrom',
        getter: () => {
          if (this.settlementDate?.start && !isNaN(new Date(this.settlementDate.start).valueOf())) {
            return startOfDay(this.settlementDate.start);
          }
        },
        setter: (value: any) => {
          if (this.settlementDate) {
            this.settlementDate.start = startOfDay(new Date(value));
          }
        },
      },
      {
        key: 'settlementDateTo',
        getter: () => {
          if (this.settlementDate?.start && !isNaN(new Date(this.settlementDate.start).valueOf())) {
            return endOfDay(this.settlementDate.end);
          }
        },
        setter: (value: any) => {
          if (this.settlementDate) {
            this.settlementDate.end = endOfDay(new Date(value));
          }
        },
      },
      {
        key: 'commissionDisplayType',
        getter: () => this.accountsFilter || undefined,
        setter: (value: any) => (this.accountsFilter = value),
      },
      {
        key: 'pageSize',
        getter: () => this.displayForm?.pageSize || undefined,
        setter: (value: any) => (this.displayForm!.pageSize = value),
      },
      {
        key: 'query',
        getter: () => this.referenceNumberOrClientName || undefined,
        setter: (value: any) => (this.referenceNumberOrClientName = value),
      },
      {
        key: 'queryBy',
        getter: () => (this.referenceNumberOrClientName && this.queryBy) || undefined,
        setter: (value: any) => (this.queryBy = value || ['clientName', 'composedReferenceNumber']),
      },
    ];
  }

  @Watch('openTrade', { immediate: true })
  onOpenTradeChange() {
    if (this.openTrade) {
      this.referenceNumberOrClientName = this.openTrade;
    }
  }
}
